import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Card, Col, Empty, Modal, Row, Space, Tabs, Tag, Tooltip, Typography, Table, Pagination } from "antd";
import { EditOutlined, TrophyOutlined, CrownOutlined, ImportOutlined } from "@ant-design/icons";
import { helper } from "@src/controls/controlHelper";
import Loader from "@src/components/Loading";
import FormCtrl from "@src/controls/layouts/schemaTemplate/FormCtrl";
import Widgets from "@src/packages/pro-component/schema/Widgets";

const { TabPane } = Tabs as any;
const { Text, Title } = Typography;

// PAGE_ID for Rank listing
const PAGE_ID = 101;
// Modal page for create/edit Rank
const MODAL_PAGE_ID = 100;
// Modal page for Import file
const IMPORT_PAGE_ID = 112;

type YearItem = number | string;

interface DictionaryItem {
  id: number;
  name?: string;
  image?: string;
}

interface RankItem {
  id: number;
  tournamentId?: number;
  year?: number;
  footballTeamId?: number;
  numberOfMatchPlayerd?: number;
  numberOfMatchWin?: number;
  numberOfMatchDrawn?: number;
  numberOfMatchLost?: number;
  totalScore?: number;
  goalDifference?: number;
  createdAt?: number;
  updatedAt?: number;
  [key: string]: any;
}

const RankCtrl: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [pageInfo, setPageInfo] = useState<any>(null);
  const [years, setYears] = useState<YearItem[]>([]);
  const [activeYear, setActiveYear] = useState<YearItem | undefined>();
  const [yearToRanks, setYearToRanks] = useState<Record<string, RankItem[]>>({});
  const [yearToTotal, setYearToTotal] = useState<Record<string, number>>({});

  // pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // search states
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  const [activeTeamIds, setActiveTeamIds] = useState<number[]>([]);

  // dictionaries
  const [tournaments, setTournaments] = useState<DictionaryItem[]>([]);
  const [teams, setTeams] = useState<DictionaryItem[]>([]);

  // modal states
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [modalQuery, setModalQuery] = useState<any>({});
  const [isShowImportModal, setIsShowImportModal] = useState<boolean>(false);

  const dictionariesById = useMemo(() => {
    const toMap = (arr: DictionaryItem[]) => arr.reduce((acc: Record<number, DictionaryItem>, cur) => { acc[cur.id] = cur; return acc; }, {});
    return {
      tournament: toMap(tournaments),
      team: toMap(teams),
    };
  }, [tournaments, teams]);

  const openCreate = useCallback(() => {
    setModalQuery({ page: MODAL_PAGE_ID, mode: "create" });
    setIsShowModal(true);
  }, []);

  const openEdit = useCallback((rankId: number) => {
    setModalQuery({ page: MODAL_PAGE_ID, mode: "edit", id: rankId });
    setIsShowModal(true);
  }, []);

  const openImport = useCallback(() => {
    setModalQuery({ page: IMPORT_PAGE_ID, mode: "create" });
    setIsShowImportModal(true);
  }, []);

  const loadYears = useCallback(async (_pageInfo: any) => {
    const rs: any = await helper.callPageApi(_pageInfo, "get_year", {});
    const data = rs?.data?.data ?? rs?.data ?? [];
    const ys: YearItem[] = Array.isArray(data) ? data : [];
    setYears(ys);
    if (ys.length > 0) setActiveYear(ys[0]);
  }, []);

  const normalizeList = (rs: any): any[] => {
    const d = rs?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  };

  const loadDictionaries = useCallback(async (_pageInfo: any) => {
    const [rt, rtm] = await Promise.all([
      helper.callPageApi(_pageInfo, "find_tournament", {}),
      helper.callPageApi(_pageInfo, "find_footballteam", {}),
    ]);
    setTournaments(normalizeList(rt) as DictionaryItem[]);
    setTeams(normalizeList(rtm) as DictionaryItem[]);
  }, []);

  const loadRanks = useCallback(async (yearKey: string) => {
    const _pageInfo = pageInfo || (await helper.getPage(PAGE_ID));
    const filter: any = { year: Number(yearKey) };
    
    // Add search filter if active
    if (activeTeamIds.length > 0) {
      filter.footballTeamId = { in: activeTeamIds };
    }
    
    const rs: any = await helper.callPageApi(_pageInfo, "find", {
      queryInput: JSON.stringify(filter),
      sort: JSON.stringify([{ totalScore: "desc" }]), // Sort by totalScore descending
      limit: pageSize,
      skip: pageSize * (currentPage - 1),
    });
    const list: RankItem[] = rs?.data?.data ?? [];
    const total = Number(rs?.data?.count ?? 0);
    
    // Add position based on totalScore ranking (considering pagination offset)
    const listWithPosition = list.map((item, index) => ({
      ...item,
      position: pageSize * (currentPage - 1) + index + 1
    }));
    
    setYearToRanks((prev) => ({ ...prev, [yearKey]: listWithPosition }));
    setYearToTotal((prev) => ({ ...prev, [yearKey]: total }));
  }, [pageInfo, activeTeamIds, currentPage, pageSize]);

  const closeModal = useCallback(() => {
    setIsShowModal(false);
    // reload active year data after closing
    if (activeYear !== undefined) {
      void loadRanks(String(activeYear));
    }
  }, [activeYear, loadRanks]);

  const closeImportModal = useCallback(() => {
    setIsShowImportModal(false);
    // reload active year data after closing
    if (activeYear !== undefined) {
      void loadRanks(String(activeYear));
    }
  }, [activeYear, loadRanks]);

  const onApplySearch = useCallback(() => {
    setActiveTeamIds(selectedTeamIds);
    setCurrentPage(1); // Reset to first page when searching
    if (activeYear !== undefined) {
      void loadRanks(String(activeYear));
    }
  }, [selectedTeamIds, activeYear, loadRanks]);

  const onResetSearch = useCallback(() => {
    setSelectedTeamIds([]);
    setActiveTeamIds([]);
    setCurrentPage(1);
    if (activeYear !== undefined) {
      void loadRanks(String(activeYear));
    }
  }, [activeYear, loadRanks]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const _pageInfo = await helper.getPage(PAGE_ID);
        setPageInfo(_pageInfo);
        await Promise.all([loadYears(_pageInfo), loadDictionaries(_pageInfo)]);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadYears, loadDictionaries]);

  useEffect(() => {
    if (activeYear !== undefined) {
      void loadRanks(String(activeYear));
    }
  }, [activeYear, loadRanks]);

  useEffect(() => {
    if (activeYear !== undefined) {
      void loadRanks(String(activeYear));
    }
  }, [currentPage, pageSize, loadRanks, activeYear]);

  const getRankIcon = (position: number) => {
    switch (position) {
      case 1:
        return <CrownOutlined style={{ color: '#ffd700', fontSize: 18 }} />;
      case 2:
        return <TrophyOutlined style={{ color: '#c0c0c0', fontSize: 18 }} />;
      case 3:
        return <TrophyOutlined style={{ color: '#cd7f32', fontSize: 18 }} />;
      default:
        return <TrophyOutlined style={{ color: '#1890ff', fontSize: 16 }} />;
    }
  };

  const getRankColor = (position: number) => {
    switch (position) {
      case 1:
        return '#ffd700';
      case 2:
        return '#c0c0c0';
      case 3:
        return '#cd7f32';
      default:
        return '#1890ff';
    }
  };

  const renderRankTable = (ranks: RankItem[]) => {
    const columns = [
      {
        title: 'Vị trí',
        dataIndex: 'position',
        key: 'position',
        width: 80,
        align: 'center' as const,
        render: (position: number) => (
          <Space align="center">
            {getRankIcon(position)}
            <Text strong style={{ color: getRankColor(position) }}>
              {position}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Đội bóng',
        dataIndex: 'footballTeamId',
        key: 'team',
        width: 200,
        render: (footballTeamId: number) => {
          const team = dictionariesById.team[footballTeamId];
          return (
            <Space align="center">
              <Avatar 
                size={32} 
                src={team?.image} 
                shape="circle"
                style={{ backgroundColor: '#1890ff' }}
              >
                {team?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Text strong>{team?.name || 'N/A'}</Text>
            </Space>
          );
        },
      },
      {
        title: 'Trận',
        dataIndex: 'numberOfMatchPlayerd',
        key: 'played',
        width: 60,
        align: 'center' as const,
        render: (played: number) => <Text>{played || 0}</Text>,
      },
      {
        title: 'Thắng',
        dataIndex: 'numberOfMatchWin',
        key: 'won',
        width: 60,
        align: 'center' as const,
        render: (won: number) => <Text style={{ color: '#52c41a' }}>{won || 0}</Text>,
      },
      {
        title: 'Hòa',
        dataIndex: 'numberOfMatchDrawn',
        key: 'drawn',
        width: 60,
        align: 'center' as const,
        render: (drawn: number) => <Text style={{ color: '#faad14' }}>{drawn || 0}</Text>,
      },
      {
        title: 'Thua',
        dataIndex: 'numberOfMatchLost',
        key: 'lost',
        width: 60,
        align: 'center' as const,
        render: (lost: number) => <Text style={{ color: '#ff4d4f' }}>{lost || 0}</Text>,
      },
      {
        title: 'Hiệu số',
        dataIndex: 'goalDifference',
        key: 'goalDifference',
        width: 80,
        align: 'center' as const,
        render: (goalDifference: number) => (
          <Text style={{ color: goalDifference >= 0 ? '#52c41a' : '#ff4d4f' }}>
            {goalDifference >= 0 ? '+' : ''}{goalDifference || 0}
          </Text>
        ),
      },
      {
        title: 'Điểm',
        dataIndex: 'totalScore',
        key: 'points',
        width: 80,
        align: 'center' as const,
        render: (totalScore: number) => (
          <Text strong style={{ color: '#1890ff', fontSize: 16 }}>
            {totalScore || 0}
          </Text>
        ),
      },
      {
        title: 'Thao tác',
        key: 'action',
        width: 80,
        align: 'center' as const,
        render: (_: any, record: RankItem) => (
          <Tooltip title="Chỉnh sửa">
            <Button
              type="text"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => openEdit(record.id)}
            />
          </Tooltip>
        ),
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={ranks}
        rowKey="id"
        pagination={false}
        size="small"
        bordered
        style={{ backgroundColor: '#fff' }}
        rowClassName={(record, index) => {
          if (index < 3) return 'top-three-row';
          return '';
        }}
      />
    );
  };

  if (loading) return <Loader />;

  return (
    <>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={24} md={12}>
                <Title level={3} style={{ margin: 0 }}>
                  Bảng xếp hạng
                </Title>
              </Col>
              <Col xs={24} md={12}>
                <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
                  <div style={{ minWidth: 300 }}>
                    <Widgets.ArraySelect
                      type="checkbox"
                      placeholder="Chọn đội bóng"
                      schema={{
                        modelSelectField: 'id,name',
                        pageId: pageInfo?.id || PAGE_ID,
                        api: 'find_footballteam',
                      }}
                      value={selectedTeamIds}
                      onChange={(values: number[]) => setSelectedTeamIds(values || [])}
                    />
                  </div>
                  <Button onClick={onResetSearch}>Reset</Button>
                  <Button type="primary" onClick={onApplySearch}>
                    Tìm kiếm
                  </Button>
                </Space>
              </Col>
            </Row>
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <Button type="primary" icon={<TrophyOutlined />} onClick={openCreate}>
                    Tạo mới
                  </Button>
                  <Button icon={<ImportOutlined />} onClick={openImport}>
                    Import file
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          <Tabs
            type="card"
            size="large"
            tabBarGutter={8}
            animated
            tabBarStyle={{ marginBottom: 16 }}
            activeKey={activeYear !== undefined ? String(activeYear) : undefined}
            onChange={(key: string) => setActiveYear(key)}
          >
            {(years || []).map((y) => {
              const key = String(y);
              const list = yearToRanks[key] || [];
              const total = yearToTotal[key] || 0;
              const isActive = String(activeYear) === key;
              
              return (
                <TabPane
                  key={key}
                  tab={
                    <Space size={6}>
                      <TrophyOutlined style={{ color: isActive ? '#1890ff' : '#8c8c8c' }} />
                      <Text strong={isActive}>{key}</Text>
                      <Tag color={isActive ? "geekblue" : "default"}>{total} đội</Tag>
                    </Space>
                  }
                >
                  <Card>
                    {list.length === 0 ? (
                      <Empty 
                        description="Không có dữ liệu xếp hạng" 
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    ) : (
                      <>
                        {renderRankTable(list)}
                        <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                          <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={yearToTotal[key] || 0}
                            showSizeChanger
                            showQuickJumper
                            showTotal={(total, range) => 
                              `${range[0]}-${range[1]} của ${total} đội`
                            }
                            pageSizeOptions={['10', '20', '50', '100']}
                            onChange={(page, size) => {
                              setCurrentPage(page);
                              setPageSize(size || pageSize);
                            }}
                          />
                        </div>
                      </>
                    )}
                  </Card>
                </TabPane>
              );
            })}
          </Tabs>
        </Col>
      </Row>

      <Modal
        width="80vw"
        visible={isShowModal}
        footer={null}
        destroyOnClose
        onCancel={closeModal}
        title={modalQuery?.mode === "edit" ? "Cập nhật Bảng xếp hạng" : "Tạo mới Bảng xếp hạng"}
      >
        {isShowModal ? (
          <FormCtrl query={modalQuery} onClose={closeModal} />
        ) : (
          <Loader />
        )}
      </Modal>

      <Modal
        width="80vw"
        visible={isShowImportModal}
        footer={null}
        destroyOnClose
        onCancel={closeImportModal}
        title="Import file bảng xếp hạng"
      >
        {isShowImportModal ? (
          <FormCtrl query={modalQuery} onClose={closeImportModal} />
        ) : (
          <Loader />
        )}
      </Modal>

    </>
  );
};

export default RankCtrl;
